import { Building2, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { type InsuranceCarrier } from '@/models/insuranceCarrier'

interface CarrierSelectProps {
  carriers: InsuranceCarrier[]
  selectedIds: string[]
  onToggle: (carrierId: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
}

function CarrierChip({ name }: { name: string }) {
  return <span className="bg-muted rounded px-1.5 py-0.5 text-xs font-medium">{name}</span>
}

export function CarrierSelect({
  carriers,
  selectedIds,
  onToggle,
  placeholder = 'Assign carriers',
  className,
  disabled,
}: CarrierSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selectedSet = new Set(selectedIds)
  const selectedCarriers = carriers.filter((c) => selectedSet.has(c.id))

  const q = query.trim().toLowerCase()
  const filtered = q ? carriers.filter((c) => c.name.toLowerCase().includes(q)) : carriers

  return (
    // `modal` so the list wheel-scrolls when opened inside a Dialog (matches TagSelect).
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn('h-auto min-h-10 w-full justify-start gap-2 py-1.5 font-normal', className)}
        >
          <Building2 className="text-muted-foreground size-4 shrink-0" />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
            {selectedCarriers.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedCarriers.map((c) => <CarrierChip key={c.id} name={c.name} />)
            )}
          </span>
          <ChevronDown className="text-muted-foreground size-4 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search carriers…" value={query} onValueChange={setQuery} />
          <CommandList>
            {filtered.length > 0 ? (
              <CommandGroup>
                {filtered.map((carrier) => (
                  <CommandItem
                    key={carrier.id}
                    value={carrier.id}
                    onSelect={() => onToggle(carrier.id)}
                    className="cursor-pointer"
                  >
                    <Checkbox checked={selectedSet.has(carrier.id)} className="pointer-events-none" />
                    <CarrierChip name={carrier.name} />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <div className="text-muted-foreground py-6 text-center text-sm">No carriers found</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
